import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/useAuthState";
import { useIsMobile, getForceDesktop } from "@/hooks/use-mobile";
import NotFound from "@/pages/not-found";
import Landing from "@/pages/landing";
import Home from "@/pages/home";
import Competitions from "@/pages/competitions";
import CompetitionDetail from "@/pages/competition-detail";
import CompetitionContinue from "@/pages/competition-continue";
import UserDashboard from "@/pages/user-dashboard";
import UserProfile from "@/pages/user-profile";

// Mobile Components
import MobileDashboard from "@/pages/mobile/MobileDashboard";
import MobileProfile from "@/pages/mobile/MobileProfile";
import MobileCompetitions from "@/pages/mobile/MobileCompetitions";
import MobileCompetitionDetail from "@/pages/mobile/MobileCompetitionDetail";
import MobileChallenges from "@/pages/mobile/MobileChallenges";
import MobileWinners from "@/pages/mobile/MobileWinners";
import MobileFriends from "@/pages/mobile/MobileFriends";

import AdminDashboard from "@/pages/admin-dashboard";
import AdminSegmentation from "@/pages/admin-segmentation";
import AdminTriggerManagement from "@/pages/admin-trigger-management";
import Roadmap from "@/pages/roadmap";
import Terms from "@/pages/terms";
import ShopriteCompetitionTerms from "@/pages/shoprite-competition-terms";
import Privacy from "@/pages/privacy";
import Disclaimer from "@/pages/disclaimer";
import FAQ from "@/pages/faq";
import Contact from "@/pages/contact";
import About from "@/pages/about";
import Blog from "@/pages/blog";
import BlogPost from "@/pages/blog-post";
import ChallengePage from "@/pages/ChallengePage";
import ChallengesListingPage from "@/pages/ChallengesListingPage";
import AuthPage from '@/pages/auth';
import FacebookLoginPage from '@/pages/facebook-login';
import Auth from '@/pages/auth';
import ForgotPasswordPage from "@/pages/forgot-password";
import ResetPasswordPage from "@/pages/reset-password";
import { EmailConfirmed } from "@/pages/EmailConfirmed";
import Winners from "@/pages/winners";
import Testimonials from "@/pages/testimonials";
import ProgressiveProfiling from "@/pages/progressive-profiling";
import GlobalTrackingCodes from "@/components/GlobalTrackingCodes";
import { PromotionalBanner } from "@/components/promotional-banner";
import { useEffect, Suspense, useState, lazy } from "react";
import { initGA, trackPageView } from "@/lib/analytics";
import { useAnalytics } from "@/hooks/use-analytics";
import { useCacheVersionCheck } from "@/components/CacheBuster"; // Added import

import { PageLoading } from "@/components/ui/loading";
import BottomTabNav from "@/components/mobile/BottomTabNav";
import MobileTopBar from "@/components/mobile/MobileTopBar";
import { PhoneVerificationGuard } from "@/components/PhoneVerificationGuard";

// Error boundary component
function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<PageLoading />}>
      {children}
    </Suspense>
  );
}

// Scroll to top component - automatically scrolls to top on route change
function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    // Scroll to top on every route change
    window.scrollTo(0, 0);
    
    // Also reset any scrollable containers
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location]);

  return null;
}

// Route mapping function for mobile/desktop conversion
function createRouteMapping() {
  // Define route patterns that should be mapped between desktop and mobile
  const routeMappings = [
    { desktop: '/', mobile: '/m' },
    { desktop: '/user', mobile: '/m' },
    { desktop: '/dashboard', mobile: '/m/dashboard' },
    { desktop: '/profile', mobile: '/m/profile' },
    { desktop: '/user/profile', mobile: '/m/profile' },
    { desktop: '/progressive-profiling', mobile: '/m/progressive-profiling' },
    { desktop: '/competitions', mobile: '/m/competitions' },
    { desktop: '/competition/:id', mobile: '/m/competition/:id' },
    { desktop: '/competition/:id/continue', mobile: '/m/competition/:id/continue' },
    { desktop: '/challenges', mobile: '/m/challenges' },
    { desktop: '/challenges/:challengeId', mobile: '/m/challenges/:challengeId' },
    { desktop: '/terms', mobile: '/m/terms' },
    { desktop: '/privacy', mobile: '/m/privacy' },
    { desktop: '/disclaimer', mobile: '/m/disclaimer' },
    { desktop: '/faq', mobile: '/m/faq' },
    { desktop: '/contact', mobile: '/m/contact' },
    { desktop: '/about', mobile: '/m/about' },
    { desktop: '/blog', mobile: '/m/blog' },
    { desktop: '/blog/:slug', mobile: '/m/blog/:slug' },
    { desktop: '/winners', mobile: '/m/winners' },
    { desktop: '/testimonials', mobile: '/m/testimonials' },
    { desktop: '/auth', mobile: '/m/auth' },
    { desktop: '/login', mobile: '/m/login' },
    { desktop: '/signup', mobile: '/m/signup' },
  ];

  function desktopToMobile(desktopPath: string): string {
    // If already on mobile route, return as-is
    if (desktopPath.startsWith('/m/') || desktopPath === '/m') {
      return desktopPath;
    }

    // Don't redirect certain paths
    const excludedPaths = ['/forgot-password', '/reset-password', '/email-confirmed'];
    if (excludedPaths.includes(desktopPath)) {
      return desktopPath;
    }

    // Don't redirect admin routes (admins should stay on desktop)
    if (desktopPath.startsWith('/admin')) {
      return desktopPath;
    }

    // Find exact match first
    const exactMapping = routeMappings.find(mapping => mapping.desktop === desktopPath);
    if (exactMapping) {
      return exactMapping.mobile;
    }

    // Handle dynamic routes with parameters
    for (const mapping of routeMappings) {
      const desktopPattern = mapping.desktop.replace(/:[^/]+/g, '([^/]+)');
      const regex = new RegExp(`^${desktopPattern}$`);
      const match = desktopPath.match(regex);

      if (match) {
        let mobilePath = mapping.mobile;
        // Replace parameters in mobile path
        match.slice(1).forEach((param, index) => {
          mobilePath = mobilePath.replace(/:[^/]+/, param);
        });
        return mobilePath;
      }
    }

    // Fallback: if no mapping found, add /m prefix if not already mobile
    if (!desktopPath.startsWith('/m')) {
      return `/m${desktopPath === '/' ? '' : desktopPath}`;
    }

    return desktopPath;
  }

  function mobileToDesktop(mobilePath: string): string {
    // If not a mobile route, return as-is
    if (!mobilePath.startsWith('/m')) {
      return mobilePath;
    }

    // Handle root mobile path
    if (mobilePath === '/m') {
      return '/';
    }

    // Remove /m prefix
    const desktopPath = mobilePath.replace(/^\/m/, '') || '/';

    // Find exact match first
    const exactMapping = routeMappings.find(mapping => mapping.mobile === mobilePath);
    if (exactMapping) {
      return exactMapping.desktop;
    }

    // Handle dynamic routes with parameters
    for (const mapping of routeMappings) {
      const mobilePattern = mapping.mobile.replace(/:[^/]+/g, '([^/]+)');
      const regex = new RegExp(`^${mobilePattern}$`);
      const match = mobilePath.match(regex);

      if (match) {
        let desktopPathResult = mapping.desktop;
        // Replace parameters in desktop path
        match.slice(1).forEach((param, index) => {
          desktopPathResult = desktopPathResult.replace(/:[^/]+/, param);
        });
        return desktopPathResult;
      }
    }

    return desktopPath;
  }

  return { desktopToMobile, mobileToDesktop };
}

// Hook for mobile redirection logic with immediate detection
function useMobileRedirection() {
  const isMobile = useIsMobile();
  const [location, navigate] = useLocation();
  const { desktopToMobile, mobileToDesktop } = createRouteMapping();

  // Perform immediate redirection check on mount to prevent desktop flash
  const [hasInitialRedirect, setHasInitialRedirect] = useState(false);

  useEffect(() => {
    if (!hasInitialRedirect) {
      // Perform immediate mobile detection without waiting for state update
      const forceDesktop = getForceDesktop();
      const currentPath = location;
      const queryString = window.location.search;

      // Quick mobile detection
      const width = window.innerWidth;
      const userAgent = navigator.userAgent;
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(userAgent);
      const isMobileWidth = width < 768;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const quickMobileDetection = isMobileUA || (isMobileWidth && isTouchDevice) || isMobileWidth;

      console.log('🚀 [APP REDIRECT] Initial redirection check:', {
        quickMobileDetection,
        forceDesktop,
        currentPath,
        queryString,
        width,
        isMobileUA
      });

      // Immediate redirect if needed
      if (quickMobileDetection && !forceDesktop) {
        const mobilePath = desktopToMobile(currentPath);
        if (mobilePath !== currentPath) {
          const mobilePathWithQuery = mobilePath + queryString;
          console.log('📱 [APP REDIRECT] Immediate mobile redirect:', {
            from: currentPath + queryString,
            to: mobilePathWithQuery
          });
          navigate(mobilePathWithQuery, { replace: true });
        }
      } else if ((!quickMobileDetection || forceDesktop) && currentPath.startsWith('/m')) {
        const desktopPath = mobileToDesktop(currentPath);
        const desktopPathWithQuery = desktopPath + queryString;
        console.log('🖥️ [APP REDIRECT] Immediate desktop redirect:', {
          from: currentPath + queryString,
          to: desktopPathWithQuery
        });
        navigate(desktopPathWithQuery, { replace: true });
      }

      setHasInitialRedirect(true);
    }
  }, [location, navigate, desktopToMobile, mobileToDesktop, hasInitialRedirect]);

  // Continue with normal redirection logic for subsequent changes
  useEffect(() => {
    if (!hasInitialRedirect) return; // Wait for initial redirect to complete

    const currentPath = location;
    const forceDesktop = getForceDesktop();
    const queryString = window.location.search;

    console.log('🔄 [APP REDIRECT] Subsequent redirection check:', {
      isMobile,
      forceDesktop,
      currentPath,
      queryString,
      isOnMobileRoute: currentPath.startsWith('/m'),
      shouldBeMobile: isMobile && !forceDesktop
    });

    // If user is on mobile device and not forcing desktop experience
    if (isMobile && !forceDesktop) {
      const mobilePath = desktopToMobile(currentPath);
      if (mobilePath !== currentPath) {
        const mobilePathWithQuery = mobilePath + queryString;
        console.log('📱 [APP REDIRECT] Redirecting to mobile:', {
          from: currentPath + queryString,
          to: mobilePathWithQuery
        });
        navigate(mobilePathWithQuery, { replace: true });
      }
    }
    // If user is on desktop device or forcing desktop experience
    else if (!isMobile || forceDesktop) {
      if (currentPath.startsWith('/m')) {
        const desktopPath = mobileToDesktop(currentPath);
        const desktopPathWithQuery = desktopPath + queryString;
        console.log('🖥️ [APP REDIRECT] Redirecting to desktop:', {
          from: currentPath + queryString,
          to: desktopPathWithQuery
        });
        navigate(desktopPathWithQuery, { replace: true });
      }
    }
  }, [isMobile, location, navigate, desktopToMobile, mobileToDesktop, hasInitialRedirect]);
}

function Router() {
  const { user, loading } = useAuth();
  const [location, navigate] = useLocation();

  // Get mobile state and force desktop setting
  const isMobile = useIsMobile();
  const forceDesktop = getForceDesktop();

  // Use mobile redirection logic
  useMobileRedirection();

  // Track page views when routes change
  useAnalytics();

  // Hook to handle cache busting
  useCacheVersionCheck(); // Added call to hook

  // Determine if user is admin based on role
  const isAdmin = user?.role === 'admin';
  const isAuthenticated = !!user;

  // FIXED: Only redirect regular users from admin routes, never redirect admins
  useEffect(() => {
    if (!loading && isAuthenticated && user?.role === 'user' && location.startsWith('/admin')) {
      navigate('/user');
    }
    // No redirect logic for admins - let them access any route
  }, [loading, isAuthenticated, user, location, navigate]);

  // Show loading state while authentication is being determined
  // This prevents 404 flash by ensuring we don't render routes until auth state is stable
  if (loading) {
    return <PageLoading />;
  }

  return (
    <ErrorBoundary>
      {/* Promotional Banner - show on all pages except admin dashboard and mobile routes */}
      {!location.startsWith('/admin') && !location.startsWith('/m') && <PromotionalBanner />}

      {/* Global Mobile Top Bar - show on all mobile routes for authenticated users, except challenge detail and competition detail pages */}
      {location.startsWith('/m') &&
       !location.match(/^\/m\/challenges\/[^/]+$/) &&
       !location.match(/^\/m\/competition\/[^/]+$/) &&
       <MobileTopBar />}

      <Switch>
        {/* Password reset routes - available to all users regardless of authentication */}
          <Route path="/forgot-password" component={ForgotPasswordPage} />
          <Route path="/reset-password" component={ResetPasswordPage} />

        {/* Email confirmation route - available to all users */}
        <Route path="/email-confirmed" component={EmailConfirmed} />
        
        {/* Unsubscribe from marketing emails */}
        <Route path="/unsubscribe" component={lazy(() => import("./pages/unsubscribe"))} />

        {/* MOBILE ROUTES - /m namespace */}
        {/* Mobile routes for unauthenticated users */}
        {!isAuthenticated && (
          <>
            <Route path="/m" component={Landing} />
            <Route path="/m/auth" component={AuthPage} />
            <Route path="/m/login" component={AuthPage} />
            <Route path="/m/signup" component={AuthPage} />
            <Route path="/m/facebook-login" component={FacebookLoginPage} />
            <Route path="/m/competitions" component={MobileCompetitions} />
            <Route path="/m/competition/:id" component={MobileCompetitionDetail} />
            <Route path="/m/challenges" component={MobileChallenges} />
            <Route path="/m/challenges/:challengeId" component={ChallengePage} />
            <Route path="/m/terms" component={Terms} />
            <Route path="/m/shoprite-competition-terms" component={ShopriteCompetitionTerms} />
            <Route path="/m/privacy" component={Privacy} />
            <Route path="/m/disclaimer" component={Disclaimer} />
            <Route path="/m/faq" component={FAQ} />
            <Route path="/m/contact" component={Contact} />
            <Route path="/m/about" component={About} />
            <Route path="/m/blog" component={Blog} />
            <Route path="/m/blog/:slug" component={BlogPost} />
            <Route path="/m/winners" component={MobileWinners} />
            <Route path="/m/testimonials" component={Testimonials} />
            <Route path="/m/friends" component={MobileFriends} />
          </>
        )}

        {/* Mobile routes for authenticated admin users */}
        {isAuthenticated && user?.role === 'admin' && (
          <>
            <Route path="/m" component={MobileDashboard} />
            <Route path="/m/admin" component={MobileDashboard} />
            <Route path="/m/admin/*" component={MobileDashboard} />
            <Route path="/m/dashboard" component={MobileDashboard} />
            <Route path="/m/user" component={MobileDashboard} />
            <Route path="/m/user/*" component={MobileDashboard} />
            <Route path="/m/profile" component={MobileProfile} />
            <Route path="/m/auth" component={MobileDashboard} />
            <Route path="/m/login" component={MobileDashboard} />
            <Route path="/m/signup" component={MobileDashboard} />
            <Route path="/m/facebook-login" component={MobileDashboard} />
            <Route path="/m/competitions" component={MobileCompetitions} />
            <Route path="/m/competition/:id" component={MobileCompetitionDetail} />
            <Route path="/m/competition/:id/continue" component={CompetitionContinue} />
            <Route path="/m/challenges" component={MobileChallenges} />
            <Route path="/m/challenges/:challengeId" component={ChallengePage} />
            <Route path="/m/terms" component={Terms} />
            <Route path="/m/shoprite-competition-terms" component={ShopriteCompetitionTerms} />
            <Route path="/m/privacy" component={Privacy} />
            <Route path="/m/disclaimer" component={Disclaimer} />
            <Route path="/m/faq" component={FAQ} />
            <Route path="/m/contact" component={Contact} />
            <Route path="/m/about" component={About} />
            <Route path="/m/blog" component={Blog} />
            <Route path="/m/blog/:slug" component={BlogPost} />
            <Route path="/m/winners" component={MobileWinners} />
            <Route path="/m/testimonials" component={Testimonials} />
            <Route path="/m/friends" component={MobileFriends} />
          </>
        )}

        {/* Mobile routes for authenticated regular users */}
        {isAuthenticated && user?.role === 'user' && (
          <>
            <Route path="/m" component={MobileDashboard} />
            <Route path="/m/dashboard" component={MobileDashboard} />
            <Route path="/m/user" component={MobileDashboard} />
            <Route path="/m/user/*" component={MobileDashboard} />
            <Route path="/m/admin" component={MobileDashboard} />
            <Route path="/m/admin/*" component={MobileDashboard} />
            <Route path="/m/auth" component={MobileDashboard} />
            <Route path="/m/login" component={MobileDashboard} />
            <Route path="/m/signup" component={MobileDashboard} />
            <Route path="/m/facebook-login" component={MobileDashboard} />
            <Route path="/m/profile" component={MobileProfile} />
            <Route path="/m/progressive-profiling" component={ProgressiveProfiling} />
            <Route path="/m/competitions" component={MobileCompetitions} />
            <Route path="/m/competition/:id" component={MobileCompetitionDetail} />
            <Route path="/m/competition/:id/continue" component={CompetitionContinue} />
            <Route path="/m/challenges" component={MobileChallenges} />
            <Route path="/m/challenges/:challengeId" component={ChallengePage} />
            <Route path="/m/terms" component={Terms} />
            <Route path="/m/shoprite-competition-terms" component={ShopriteCompetitionTerms} />
            <Route path="/m/privacy" component={Privacy} />
            <Route path="/m/disclaimer" component={Disclaimer} />
            <Route path="/m/faq" component={FAQ} />
            <Route path="/m/contact" component={Contact} />
            <Route path="/m/about" component={About} />
            <Route path="/m/blog" component={Blog} />
            <Route path="/m/blog/:slug" component={BlogPost} />
            <Route path="/m/winners" component={MobileWinners} />
            <Route path="/m/testimonials" component={Testimonials} />
            <Route path="/m/friends" component={MobileFriends} />
          </>
        )}

        {!isAuthenticated ? (
          <>
            <Route path="/" component={Landing} />
            <Route path="/auth" component={AuthPage} />
            <Route path="/login" component={AuthPage} />
            <Route path="/signup" component={AuthPage} />
            <Route path="/facebook-login" component={FacebookLoginPage} />
            <Route path="/competitions" component={Competitions} />
            {/* Only render desktop competition route if not mobile or force desktop is enabled */}
            {(!isMobile || forceDesktop) && <Route path="/competition/:id" component={CompetitionDetail} />}
            <Route path="/challenges" component={ChallengesListingPage} />
            <Route path="/challenges/:challengeId" component={ChallengePage} />
            <Route path="/terms" component={Terms} />
            <Route path="/shoprite-competition-terms" component={ShopriteCompetitionTerms} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/disclaimer" component={Disclaimer} />
            <Route path="/faq" component={FAQ} />
            <Route path="/contact" component={Contact} />
            <Route path="/about" component={About} />
            <Route path="/blog" component={Blog} />
            <Route path="/blog/:slug" component={BlogPost} />
            <Route path="/winners" component={Winners} />
            <Route path="/testimonials" component={Testimonials} />
            <Route path="/user" component={AuthPage} />
            <Route path="/admin" component={AuthPage} />

            {/* Mobile routes for non-authenticated users */}
            <Route path="/m" component={Landing} />
            <Route path="/m/competitions" component={MobileCompetitions} />
            <Route path="/m/competition/:id" component={MobileCompetitionDetail} />
            <Route path="/m/challenges" component={MobileChallenges} />
            <Route path="/m/challenges/:challengeId" component={ChallengePage} />
            <Route path="/m/terms" component={Terms} />
            <Route path="/m/shoprite-competition-terms" component={ShopriteCompetitionTerms} />
            <Route path="/m/privacy" component={Privacy} />
            <Route path="/m/disclaimer" component={Disclaimer} />
            <Route path="/m/faq" component={FAQ} />
            <Route path="/m/contact" component={Contact} />
            <Route path="/m/about" component={About} />
            <Route path="/m/blog" component={Blog} />
            <Route path="/m/blog/:slug" component={BlogPost} />
            <Route path="/m/winners" component={MobileWinners} />
            <Route path="/m/testimonials" component={Testimonials} />
            <Route path="/m/friends" component={MobileFriends} />
            <Route path="/m/auth" component={AuthPage} />
            <Route path="/m/login" component={AuthPage} />
            <Route path="/m/signup" component={AuthPage} />

            {/* Protected mobile routes that redirect to auth for logged-out users */}
            <Route path="/m/profile" component={AuthPage} />
            <Route path="/m/dashboard" component={AuthPage} />
            <Route path="/m/user" component={AuthPage} />
          </>
        ) : isAuthenticated && user?.role === 'admin' ? (
          // Admin routes - fast direct loading
          <>
            <Route path="/" component={AdminDashboard} />
            <Route path="/admin" component={AdminDashboard} />
            <Route path="/admin/*" component={AdminDashboard} />
            <Route path="/admin-dashboard" component={AdminDashboard} />
            <Route path="/admin-segmentation" component={AdminSegmentation} />
            <Route path="/dashboard" component={AdminDashboard} />
            <Route path="/user" component={AdminDashboard} />
            <Route path="/user/*" component={AdminDashboard} />
            <Route path="/profile" component={AdminDashboard} />
            <Route path="/user/profile" component={AdminDashboard} />
            <Route path="/auth" component={AdminDashboard} />
            <Route path="/login" component={AdminDashboard} />
            <Route path="/signup" component={AdminDashboard} />
            <Route path="/facebook-login" component={AdminDashboard} />
            <Route path="/competitions" component={Competitions} />
            {/* Only render desktop competition route if not mobile or force desktop is enabled */}
            {(!isMobile || forceDesktop) && <Route path="/competition/:id" component={CompetitionDetail} />}
            {(!isMobile || forceDesktop) && <Route path="/competition/:id/continue" component={CompetitionContinue} />}
            <Route path="/challenges" component={ChallengesListingPage} />
            <Route path="/challenges/:challengeId" component={ChallengePage} />
            <Route path="/admin/triggers" component={AdminTriggerManagement} />

            <Route path="/roadmap" component={Roadmap} />
            <Route path="/terms" component={Terms} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/disclaimer" component={Disclaimer} />
            <Route path="/faq" component={FAQ} />
            <Route path="/contact" component={Contact} />
            <Route path="/about" component={About} />
            <Route path="/blog" component={Blog} />
            <Route path="/blog/:slug" component={BlogPost} />
            <Route path="/winners" component={Winners} />
            <Route path="/testimonials" component={Testimonials} />
          </>
        ) : isAuthenticated && user?.role === 'user' ? (
          // Regular user routes - redirect to enhanced dashboard
          <>
            <Route path="/" component={UserDashboard} />
            <Route path="/user" component={UserDashboard} />
            <Route path="/user/*" component={UserDashboard} />
            <Route path="/dashboard" component={UserDashboard} />
            <Route path="/admin" component={UserDashboard} />
            <Route path="/admin/*" component={UserDashboard} />
            <Route path="/auth" component={UserDashboard} />
            <Route path="/login" component={UserDashboard} />
            <Route path="/signup" component={UserDashboard} />
            <Route path="/facebook-login" component={UserDashboard} />
            <Route path="/profile" component={UserProfile} />
            <Route path="/user/profile" component={UserProfile} />
            <Route path="/progressive-profiling" component={ProgressiveProfiling} />
            <Route path="/competitions" component={Competitions} />
            {/* Only render desktop competition route if not mobile or force desktop is enabled */}
            {(!isMobile || forceDesktop) && <Route path="/competition/:id" component={CompetitionDetail} />}
            {(!isMobile || forceDesktop) && <Route path="/competition/:id/continue" component={CompetitionContinue} />}
            <Route path="/challenges" component={ChallengesListingPage} />
            <Route path="/challenges/:challengeId" component={ChallengePage} />

            <Route path="/terms" component={Terms} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/disclaimer" component={Disclaimer} />
            <Route path="/faq" component={FAQ} />
            <Route path="/contact" component={Contact} />
            <Route path="/about" component={About} />
            <Route path="/blog" component={Blog} />
            <Route path="/blog/:slug" component={BlogPost} />
            <Route path="/winners" component={Winners} />
            <Route path="/testimonials" component={Testimonials} />
          </>
        ) : null}
        {/* Only show 404 after loading is complete to prevent flashing */}
        <Route component={NotFound} />
      </Switch>

      {/* Global Mobile Bottom Navigation - show on all mobile routes */}
      {location.startsWith('/m') && <BottomTabNav />}
    </ErrorBoundary>
  );
}

function App() {
  // Initialize Google Analytics
  useEffect(() => {
    console.log('[APP] Initializing Google Analytics...');
    initGA();

    // Track initial page view
    if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
      trackPageView(window.location.pathname);
    } else {
      console.warn('Missing required Google Analytics key: VITE_GA_MEASUREMENT_ID');
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PhoneVerificationGuard>
          <TooltipProvider>
            <ScrollToTop />
            <GlobalTrackingCodes />
            <Toaster />
            <Router />
          </TooltipProvider>
        </PhoneVerificationGuard>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
